/**
 * Anatomy — the parametric bird.
 *
 * One drawing serves all six archetypes. `Proportions` supplies twenty-two numbers and
 * this file turns them into a skeleton of Q8 joints and then into materials on a grid.
 * There is no per-archetype branch anywhere below, which is the whole reason the trait
 * is worth having: a Wader is a Robin whose `legLen` and `neckLen` were drawn long, and
 * if the code knew the difference the trait would be six drawings with a label on top.
 *
 * **Two stages, on purpose.** `buildSkeleton` is pure arithmetic and returns every joint
 * the parts hang from; `drawCreature` rasterises them. Keeping them apart means placement
 * can be asserted numerically — feet exactly on the ground row, body centred — without
 * inspecting pixels, and the Solidity mirror can share the struct.
 *
 * **The body is centred, not the silhouette.** A Corvid's tail is six cells long and its
 * beak two, so centring the bounding box would slide the head a cell and a half right of
 * where a Finch's sits. Centring the *body* instead puts every bird's eye in nearly the
 * same place across the collection, which is what makes a grid of these read as one set.
 * The cost is that the frame margin is uneven front to back, which nobody looking at a
 * single token can see.
 *
 * **The feet are placed, not drawn where they fall.** Both ankles land on
 * `GROUND_ROW`'s cell centre regardless of leg length, so a Wader stands on the same line
 * as an Owl. Terrain does not lift them; see `GROUND_ROW` in `terrain.ts` for why.
 *
 * **No `atan`, anywhere.** Every angle in the drawing is an authored trait, never one
 * recovered from two points, so `sin`/`cos` on a 256-step table is the only trigonometry
 * needed. That is what keeps `Anatomy.sol` free of an inverse-trig approximation.
 *
 * **Sub-cell features are reported, not silently dropped.** A Finch's beak is under a
 * cell long and a Robin's eye is a third of one; at 32x32 they can miss every sample
 * point. Each such part has a stated fallback — a line for the beak, a single cell for
 * the eye — and `CreatureNotes` records which ones fired, so `tools/creaturecheck.ts`
 * can report it instead of a reviewer discovering an eyeless bird by eye.
 */

import { GRID_COLS, GRID_ROWS, SUB } from "../constants.js";
import type { Archetype, ArchetypeRangeField } from "../data/archetypes.js";
import { ARCHETYPES, ARCHETYPE_RANGE_FIELDS } from "../data/archetypes.js";
import { idiv, shl, shr } from "../engine/int.js";
import { cos, sin } from "../engine/trig.js";
import {
  MAT_BEAK,
  MAT_BREAST,
  MAT_CREST,
  MAT_EMPTY,
  MAT_EYE,
  MAT_LEG,
  MAT_OUTLINE,
  MAT_PLUMAGE_DARK,
  MAT_PLUMAGE_LIGHT,
  MAT_PLUMAGE_MID,
  MAT_PRIMARY,
  MAT_PUPIL,
  MAT_TAIL,
  MAT_WING,
  seedsOutline,
} from "./materials.js";
import type { MaterialGrid } from "./raster.js";
import {
  cellCentre,
  countDrawn,
  countMaterial,
  drawnBounds,
  fillDisc,
  fillEllipse,
  fillTriangle,
  materialAt,
  newMaterialGrid,
  outlinePass,
  plot,
  strokeLine,
} from "./raster.js";
import { GROUND_ROW } from "./terrain.js";
import type { Proportions } from "./traits.js";

/**
 * Q8 x the body's centre sits on: the grid's exact middle, on a cell *boundary*.
 *
 * A boundary rather than a cell centre, so the body covers the same number of cells on
 * each side of it. Centred on a cell instead, every ellipse would be one cell wider on
 * one side than the other and the whole collection would lean.
 */
export const CENTRE_X = (GRID_COLS / 2) * SUB;

/** Q8 y both ankles land on. */
export const GROUND_Y = cellCentre(GROUND_ROW);

/**
 * Shortest feature that is allowed to be drawn at all, Q8.
 *
 * Half a cell. Below it a crest is one stray light cell on the crown, which reads as a
 * rendering fault rather than a tuft — so a crest shorter than this is not drawn, and
 * `crestDrawn` says so. The beak and the eye have real fallbacks instead, because a bird
 * with no beak or no eye is not a bird; a bird with no crest is most birds.
 */
const LEGIBLE = SUB / 2;

/**
 * Eye cells needed before a pupil is worth drawing over them.
 *
 * Two, because the pupil replaces the centre cell: at one cell the pupil *is* the eye and
 * the highlight it exists to make is invisible, and at three the threshold was unreachable
 * — measured over 1024 tokens it drew on none of them, an Owl included, because an eye of
 * 0.8 cells cannot cover three. Two gives the Owl a dark cell inside a bright one, which
 * at this size is the whole of a face.
 */
const PUPIL_MIN_EYE = 2;

/** First primary's angle below horizontal, and the step between them, in trig steps. */
const PRIMARY_BASE = 4;
const PRIMARY_STEP = 5;

/** Ear tufts tilt this far from vertical, outward from the head's centre line. */
const CREST_TILT = 14;

/** `len * cos(angle)`, Q8 in and Q8 out. */
function along(len: number, angle: number): number {
  return shr(len * cos(angle), 12);
}

/** `len * sin(angle)`, Q8 in and Q8 out. */
function across(len: number, angle: number): number {
  return shr(len * sin(angle), 12);
}

/** `x * f / 256`, for the 0..256 fractions the archetype table uses. */
function frac(x: number, f: number): number {
  return shr(x * f, 8);
}

/** Midpoint of two Q8 coordinates. Floors, like `SAR`, so it never returns `-0`. */
function mid(a: number, b: number): number {
  return a + shr(b - a, 1);
}

/**
 * Every joint the drawing hangs from, in Q8 grid coordinates.
 *
 * Named after the part that owns it rather than after a numbered bone, because the only
 * consumers are the eight draw calls below and the tests that assert placement.
 */
export interface Skeleton {
  readonly bodyCx: number;
  readonly bodyCy: number;
  /** Body ellipse's top and underside on the centre line. The underside is the hip. */
  readonly bodyTopY: number;
  readonly hipY: number;

  readonly neckBaseX: number;
  readonly headCx: number;
  readonly headCy: number;

  readonly beakBaseX: number;
  readonly beakBaseY: number;
  readonly beakTipX: number;
  readonly beakTipY: number;

  readonly eyeCx: number;
  readonly eyeCy: number;

  readonly tailRootX: number;
  readonly tailRootY: number;
  readonly tailTipX: number;
  readonly tailTipY: number;

  /** Front of the wing's fold. Its depth is the body's, so only the column is a joint. */
  readonly shoulderX: number;
  readonly wingTipX: number;
  readonly wingTipY: number;

  /** The two ankles, both on `GROUND_Y`. Fore is the leg nearer the beak. */
  readonly rearAnkleX: number;
  readonly foreAnkleX: number;
}

/**
 * Place every joint from the twenty-two proportions.
 *
 * The chain runs upward from the ground, which is what makes the feet exact: the body
 * centre is `legLen + bodyH` above `GROUND_Y` by construction rather than by correction.
 * `drawnExtent()` in `traits.ts` measures the same chain — ground to hip is `legLen`, hip
 * to body top is `2 * bodyH`, body top to head underside is `neckLen`, and the head is
 * `2 * headR` — so the two agree by sharing an interpretation, not by coincidence.
 */
export function buildSkeleton(p: Proportions): Skeleton {
  const bodyCx = CENTRE_X;
  const hipY = GROUND_Y - p.legLen;
  const bodyCy = hipY - p.bodyH;
  const bodyTopY = bodyCy - p.bodyH;

  // The neck rises from the body's front shoulder, not from its middle. That single
  // offset is most of what makes the silhouette read as facing somewhere.
  const neckBaseX = bodyCx + shr(p.bodyW, 1);
  const reach = p.neckLen + p.headR;
  const headCx = neckBaseX + across(reach, p.neckAngle);
  const headCy = bodyTopY - along(reach, p.neckAngle);

  // The beak starts inside the head so its base is always covered, and sits a little
  // below the eye — a beak on the centre line reads as a nose.
  const beakBaseX = headCx + frac(p.headR, 200);
  const beakBaseY = headCy + frac(p.headR, 40);
  const beakTipX = beakBaseX + p.beakLen;
  const beakTipY = beakBaseY + frac(p.beakLen, p.beakHook);

  const eyeCx = headCx + frac(p.headR, 96);
  const eyeCy = headCy - frac(p.headR, 64);

  const tailRootX = bodyCx - p.bodyW;
  const tailRootY = bodyCy + frac(p.bodyH, 64);
  const tailTipX = tailRootX - along(p.tailLen, p.tailAngle);
  const tailTipY = tailRootY + across(p.tailLen, p.tailAngle);

  const shoulderX = bodyCx + shr(p.bodyW, 2);
  const wingTipX = shoulderX - p.wingLen;
  // Measured from the body's midline, which is what `wingDrop` is documented to mean.
  // Hung off a shoulder a quarter of the body's depth up, that quarter cancelled the drop
  // and every archetype's tip came out *above* centre, Raptor's authored 448 included.
  const wingTipY = bodyCy + p.wingDrop;

  /**
   * The ankles take the *whole* of `legSpread` either side of centre, not half of it.
   *
   * Halving it first was the obvious reading of the field, and measurement killed it:
   * `CENTRE_X` sits on the boundary between cells 15 and 16, so any half-stance under
   * 256 Q8 puts the two ankles in exactly those two cells — which every archetype's
   * halved `legSpread` did, Raptor's authored 416 included. Six archetypes, one stance.
   * Using the full value separates Raptor's feet by a visible gap and leaves the narrow
   * standers (Wader, Finch) touching, which is what the data was authored to say.
   */
  const stance = p.legSpread;

  return {
    bodyCx,
    bodyCy,
    bodyTopY,
    hipY,
    neckBaseX,
    headCx,
    headCy,
    beakBaseX,
    beakBaseY,
    beakTipX,
    beakTipY,
    eyeCx,
    eyeCy,
    tailRootX,
    tailRootY,
    tailTipX,
    tailTipY,
    shoulderX,
    wingTipX,
    wingTipY,
    rearAnkleX: bodyCx - stance,
    foreAnkleX: bodyCx + stance,
  };
}

/** Which sub-cell outcomes fired. Diagnostic only; nothing renders from it. */
export interface CreatureNotes {
  /** The beak is its spine alone: the depth triangle covered no cell centre. */
  readonly beakSpineOnly: boolean;
  /** The eye disc covered no cell, so a single cell was plotted instead. */
  readonly eyeFallback: boolean;
  /** Cells the eye ended up with, fallback included. Never zero. */
  readonly eyeCells: number;
  /** False when `crestLen` was below `LEGIBLE`, which is most birds. */
  readonly crestDrawn: boolean;
  /** True only when the eye was wide enough to carry one. Effectively an Owl. */
  readonly pupilDrawn: boolean;
}

export interface Creature {
  /** A creature-only material grid. `compose.ts` layers this over the terrain. */
  readonly grid: MaterialGrid;
  readonly skeleton: Skeleton;
  /** Bounds of the bird itself, before the outline. */
  readonly bodyBounds: { x0: number; y0: number; x1: number; y1: number } | null;
  /** Bounds including the outline — the box that has to fit the frame. */
  readonly bounds: { x0: number; y0: number; x1: number; y1: number } | null;
  readonly bodyCells: number;
  readonly outlineCells: number;
  readonly notes: CreatureNotes;
}

/**
 * Draw one bird.
 *
 * `tone` is the leading plumage material — one of `MAT_PLUMAGE_DARK`, `_MID` or `_LIGHT`.
 * The crown cap is derived from it rather than passed in, so head and body can never be
 * authored the same colour: a dark bird gets a light crown and everything else a dark one.
 *
 * The order is a painter's order and every line of it matters. Tail and legs go down
 * first so the body buries their roots; the wing and the breast are masked to `tone` so
 * neither can widen the silhouette they sit on; the neck is *not* masked, because a neck
 * is part of the outline; and the outline pass runs last over a grid that holds nothing
 * but the bird, which is why anatomy owns a grid instead of drawing into the scene. It
 * grows only from the parts `seedsOutline` admits: the legs and the beak are already the
 * rim's colour, so rimming them would widen them rather than edge them.
 */
export function drawCreature(p: Proportions, tone: number): Creature {
  const g = newMaterialGrid();
  const s = buildSkeleton(p);
  const cap = tone === MAT_PLUMAGE_DARK ? MAT_PLUMAGE_LIGHT : MAT_PLUMAGE_DARK;

  drawTail(g, p, s);
  drawLegs(g, p, s);
  fillEllipse(g, tone, s.bodyCx, s.bodyCy, p.bodyW, p.bodyH, p.lean);
  drawWing(g, p, s, tone);
  drawPrimaries(g, p, s);
  drawBreast(g, p, s, tone);
  drawNeck(g, p, s, tone);
  fillDisc(g, tone, s.headCx, s.headCy, p.headR);
  drawCap(g, p, s, cap, tone);
  const crestDrawn = drawCrest(g, p, s);
  const beakSpineOnly = drawBeak(g, p, s);

  let eyeCells = fillDisc(g, MAT_EYE, s.eyeCx, s.eyeCy, p.eyeR);
  const eyeFallback = eyeCells === 0;
  if (eyeFallback) eyeCells = plot(g, s.eyeCx, s.eyeCy, MAT_EYE);
  const pupilDrawn = eyeCells >= PUPIL_MIN_EYE && plot(g, s.eyeCx, s.eyeCy, MAT_PUPIL) > 0;

  const bodyCells = countDrawn(g);
  const bodyBounds = drawnBounds(g);
  const outlineCells = outlinePass(g, MAT_OUTLINE, seedsOutline);

  return {
    grid: g,
    skeleton: s,
    bodyBounds,
    bounds: drawnBounds(g),
    bodyCells,
    outlineCells,
    notes: { beakSpineOnly, eyeFallback, eyeCells, crestDrawn, pupilDrawn },
  };
}

/**
 * Tail: a solid wedge between the two outermost feathers, with the feather lines drawn
 * on top of it.
 *
 * The wedge is what gives a tail mass at this size; the three strokes are what stop a
 * narrow fan from vanishing, because a wedge two cells wide can miss every cell centre
 * while its edges cannot. `tailSpread` of zero collapses the wedge to nothing and leaves
 * a single spike, which is the authored intent for a Corvid.
 */
function drawTail(g: MaterialGrid, p: Proportions, s: Skeleton): void {
  const lo = p.tailAngle - p.tailSpread;
  const hi = p.tailAngle + p.tailSpread;
  const loX = s.tailRootX - along(p.tailLen, lo);
  const loY = s.tailRootY + across(p.tailLen, lo);
  const hiX = s.tailRootX - along(p.tailLen, hi);
  const hiY = s.tailRootY + across(p.tailLen, hi);

  fillTriangle(g, MAT_TAIL, s.tailRootX, s.tailRootY, loX, loY, hiX, hiY);
  strokeLine(g, MAT_TAIL, s.tailRootX, s.tailRootY, loX, loY);
  strokeLine(g, MAT_TAIL, s.tailRootX, s.tailRootY, hiX, hiY);
  strokeLine(g, MAT_TAIL, s.tailRootX, s.tailRootY, s.tailTipX, s.tailTipY);
}

/**
 * Legs: one stroke each from a hip half way out to the ankle, plus a foot across the
 * ground row.
 *
 * Two legs at the same height read as one leg in front of the other, which is what a
 * standing bird in profile looks like. Splaying the hips only half as far as the ankles
 * is what makes them read as legs rather than as a pair of parallel sticks.
 */
function drawLegs(g: MaterialGrid, p: Proportions, s: Skeleton): void {
  for (const ankleX of [s.rearAnkleX, s.foreAnkleX]) {
    strokeLine(g, MAT_LEG, mid(s.bodyCx, ankleX), s.hipY, ankleX, GROUND_Y);
    strokeLine(g, MAT_LEG, ankleX - p.footSpan, GROUND_Y, ankleX + p.footSpan, GROUND_Y);
  }
}

/**
 * Folded wing: a wedge from a vertical shoulder edge back to the wing tip, masked to the
 * body.
 *
 * Masked, so the wing can never poke past the back or below the belly — a triangle corner
 * outside the silhouette reads as damage, not as a wing. The fold still separates because
 * `MAT_WING` is the heaviest ramp step against the body's sixth, so it works even on a
 * palette where dark plumage and the body tone are a step apart.
 *
 * The shoulder edge spans the body's whole depth plus `wingDrop` rather than half of it,
 * which is where this started: half a body deep, the wedge covered five cells of a
 * ninety-cell Robin and read as a smudge behind the breast. The mask is what makes
 * overshooting free — anything past the silhouette is simply refused — so the edge is
 * drawn as tall as the wing plausibly reaches and the body decides where it ends.
 */
function drawWing(g: MaterialGrid, p: Proportions, s: Skeleton, tone: number): number {
  const half = shr(p.bodyH, 1);
  return fillTriangle(
    g,
    MAT_WING,
    s.shoulderX,
    s.bodyCy - half,
    s.shoulderX,
    s.bodyCy + half + p.wingDrop,
    s.wingTipX,
    s.wingTipY,
    tone,
  );
}

/**
 * Primaries: a fan of ink lines from the wing tip, backward and down.
 *
 * Unmasked, unlike the wing they belong to. Where they stay inside the body they read as
 * the separation between folded feathers, and where they run past the rear they read as
 * the tips crossing the tail. Both are what a folded wing does, so no clipping is needed
 * — the length is half the wing, which is short enough that neither reading dominates.
 */
function drawPrimaries(g: MaterialGrid, p: Proportions, s: Skeleton): void {
  const len = shr(p.wingLen, 1);
  for (let i = 0; i < p.primaries; i++) {
    const angle = PRIMARY_BASE + i * PRIMARY_STEP;
    strokeLine(
      g,
      MAT_PRIMARY,
      s.wingTipX,
      s.wingTipY,
      s.wingTipX - along(len, angle),
      s.wingTipY + across(len, angle),
    );
  }
}

/**
 * Breast patch: a disc on the body's lower front, masked to the body.
 *
 * `breastExtent` scales it against `bodyW`, so a Robin at 224/256 carries almost the whole
 * front and a Wader at 96 carries a bib. Masked for the same reason as the wing, and it
 * matters more here: this is the loudest colour in the palette, so a cell of it outside
 * the silhouette would be the first thing anyone saw.
 */
function drawBreast(g: MaterialGrid, p: Proportions, s: Skeleton, tone: number): number {
  const r = frac(p.bodyW, p.breastExtent);
  if (r <= 0) return 0;
  return fillDisc(g, MAT_BREAST, s.bodyCx + shr(p.bodyW, 1), s.bodyCy + shr(p.bodyH, 1), r, tone);
}

/**
 * Neck: one ellipse from the shoulder to the head, half a head wide.
 *
 * A stroked line was the first attempt and it is wrong at this size — a one-cell neck on a
 * Wader looks like the head came loose. An ellipse tilted by `neckAngle` gives the column
 * the same width whatever the length, and collapses to nothing for an Owl, whose `neckLen`
 * is authored at zero to two tenths of a cell.
 */
function drawNeck(g: MaterialGrid, p: Proportions, s: Skeleton, tone: number): number {
  const half = shr(p.neckLen + p.headR, 1);
  return fillEllipse(
    g,
    tone,
    mid(s.neckBaseX, s.headCx),
    mid(s.bodyTopY, s.headCy),
    shr(p.headR, 1),
    half,
    p.neckAngle,
  );
}

/**
 * Crown cap: the upper half of the head in the contrasting plumage, masked to the head.
 *
 * Masked to `tone`, which at this point in the order means "the head or the neck", both of
 * which are the right place for it. Without a cap the head is the same solid block as the
 * body and a short-necked bird loses its head entirely; one ellipse buys the separation
 * for every archetype at once.
 */
function drawCap(g: MaterialGrid, p: Proportions, s: Skeleton, cap: number, tone: number): number {
  const h = shr(p.headR, 1);
  return fillEllipse(g, cap, s.headCx, s.headCy - h, p.headR, h, 0, tone);
}

/**
 * Crest: two ear tufts from the top of the head, tilting outward.
 *
 * Two rather than one, because a single central spike reads as an antenna while a pair
 * reads as a bird — and because the archetype that always has one is the Owl. Skipped
 * entirely below `LEGIBLE`; see that constant for why a stub is worse than nothing.
 */
function drawCrest(g: MaterialGrid, p: Proportions, s: Skeleton): boolean {
  if (p.crestLen < LEGIBLE) return false;
  const rootY = s.headCy - p.headR + shr(p.headR, 2);
  const dx = shr(p.headR, 1);
  const rise = along(p.crestLen, CREST_TILT);
  const lean = across(p.crestLen, CREST_TILT);
  let drawn = 0;
  drawn += strokeLine(g, MAT_CREST, s.headCx - dx, rootY, s.headCx - dx - lean, rootY - rise);
  drawn += strokeLine(g, MAT_CREST, s.headCx + dx, rootY, s.headCx + dx + lean, rootY - rise);
  return drawn > 0;
}

/**
 * Beak: a ridge from a base inside the head forward to the tip's column, a drop from there
 * to the hooked tip, and a triangle over both for depth near the base.
 *
 * The ridge and the drop are drawn *always*, not as a fallback, and that is the whole
 * design of this part. Measured on the mid proportions of all six archetypes, the triangle
 * alone covered exactly one cell for five of them — a Corvid's 1.9-cell bill and a Robin's
 * 1.3-cell bill were the same single cell, because a wedge tapering to a point only catches
 * a cell centre where it is still wide. Whether `beakLen` was visible at all came down to
 * where the lattice happened to fall.
 *
 * **Both strokes are axis-aligned, and that is not a style choice.** A stroke that steps
 * diagonally leaves two cells touching only at a corner, so the tip is a separate piece of
 * beak — it renders as a dot floating a cell off the face, and when the outline still grew
 * from keratin the rim filled the corner and framed the gap instead of hiding it. A single
 * sloped stroke to the tip did this on Raptor and Corvid at their mid proportions; adding
 * the ridge hid it there and did not fix it, because token 64 of the sample is a Raptor
 * whose beak still came apart at (20,14)/(21,15). An L — upper mandible along `beakBaseY`,
 * then straight down the tip's column — cannot produce a diagonal step at all, and it draws
 * `beakHook` as a hooked point rather than as a tilted stick.
 *
 * Returns whether the beak is those two strokes alone, the triangle having covered nothing.
 * That is a real outcome for a short deep beak, not a defect; `creaturecheck` reports it.
 */
function drawBeak(g: MaterialGrid, p: Proportions, s: Skeleton): boolean {
  strokeLine(g, MAT_BEAK, s.beakBaseX, s.beakBaseY, s.beakTipX, s.beakBaseY);
  strokeLine(g, MAT_BEAK, s.beakTipX, s.beakBaseY, s.beakTipX, s.beakTipY);
  const depth = fillTriangle(
    g,
    MAT_BEAK,
    s.beakBaseX,
    s.beakBaseY - p.beakDepth,
    s.beakBaseX,
    s.beakBaseY + p.beakDepth,
    s.beakTipX,
    s.beakTipY,
  );
  return depth === 0;
}

/** Where in an archetype's ranges to take every field from at once. */
export type RangeCorner = "min" | "mid" | "max";

/**
 * Every field of an archetype at the same point in its range.
 *
 * No real token looks like this — the RNG draws each field independently — but the corners
 * are exactly where a layout breaks, so the guard and `tools/creaturecheck.ts` both want
 * them. `mid` is the archetype's own portrait.
 */
export function proportionsAt(a: Archetype, at: RangeCorner): Proportions {
  const out = {} as Record<ArchetypeRangeField, number>;
  for (const field of ARCHETYPE_RANGE_FIELDS) {
    const [lo, hi] = a[field];
    out[field] = at === "min" ? lo : at === "max" ? hi : lo + shr(hi - lo, 1);
  }
  return out;
}

/**
 * Structural guard invoked by tests.
 *
 * Walks all six archetypes at all three corners of their ranges — eighteen birds — and
 * asserts the things that make a drawing a bird rather than a blob: it stands on the
 * ground line, it fits the frame with room for its own outline, and it has exactly one
 * each of the features a viewer looks for first.
 */
export function validateAnatomy(): void {
  if (GROUND_Y !== cellCentre(GROUND_ROW)) throw new Error("the ground line moved");
  if (CENTRE_X !== shl(GRID_COLS, 7)) throw new Error("the body is not centred on the grid");

  for (const a of ARCHETYPES) {
    for (const at of ["min", "mid", "max"] as const) {
      const p = proportionsAt(a, at);
      const who = `${a.name}/${at}`;
      const c = drawCreature(p, MAT_PLUMAGE_MID);
      const b = c.bounds;
      if (b === null) throw new Error(`${who}: drew nothing at all`);

      if (b.x0 < 0 || b.y0 < 0 || b.x1 >= GRID_COLS || b.y1 >= GRID_ROWS) {
        throw new Error(`${who}: bounds ${JSON.stringify(b)} leave the frame`);
      }
      const body = c.bodyBounds as { x0: number; y0: number; x1: number; y1: number };
      if (body.x0 < 1 || body.y0 < 1 || body.x1 > GRID_COLS - 2 || body.y1 > GRID_ROWS - 2) {
        throw new Error(`${who}: body ${JSON.stringify(body)} leaves no room for its outline`);
      }

      // Feet on the line. Measured, not assumed: `legLen` is only the top of the chain.
      let onGround = 0;
      for (let x = 0; x < GRID_COLS; x++) if (materialAt(c.grid, x, GROUND_ROW) !== MAT_EMPTY) onGround++;
      if (onGround === 0) throw new Error(`${who}: nothing touches the ground row`);
      if (countMaterial(c.grid, MAT_LEG) === 0) throw new Error(`${who}: has no legs`);

      if (c.notes.eyeCells < 1) throw new Error(`${who}: has no eye`);
      if (countMaterial(c.grid, MAT_BEAK) === 0) throw new Error(`${who}: has no beak`);
      if (countMaterial(c.grid, MAT_TAIL) === 0) throw new Error(`${who}: has no tail`);
      if (countMaterial(c.grid, MAT_BREAST) === 0) throw new Error(`${who}: has no breast patch`);
      if (c.outlineCells === 0) throw new Error(`${who}: was not outlined`);

      // The eye is drawn unmasked, so nothing but arithmetic stops it widening the head it
      // is meant to sit in. `archetypes.ts` holds `eyeR` at or under half the smallest
      // `headR` for exactly this; the offsets that make the promise real are here, so the
      // check belongs here too, exactly and in integers.
      const s = buildSkeleton(p);
      const dx = s.eyeCx - s.headCx;
      const dy = s.eyeCy - s.headCy;
      const room = p.headR - p.eyeR;
      if (room < 0 || dx * dx + dy * dy > room * room) {
        throw new Error(`${who}: the eye disc reaches outside the head`);
      }

      // The body ellipse alone owns this many cells, so the whole bird cannot own fewer.
      // Derived from the proportions rather than picked: 3 stands in for pi from below, and
      // parts drawn *inside* the body replace cells instead of adding them, so the ellipse
      // is a true floor. A masking mistake that erased the body would fail here.
      const floor = idiv(3 * p.bodyW * p.bodyH, SUB * SUB);
      if (c.bodyCells < floor) {
        throw new Error(`${who}: ${c.bodyCells} cells of bird, under the ${floor} its body covers`);
      }
    }
  }
}

